import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from "@angular/core";
import Quill from "../../../../components/mvs-quill-editor/data/quill-config";
import {Delta} from "quill";
import hljs from 'highlight.js';
import {QuillHelper} from "../../../../helper/quill-helper";
import {DiagramBlot} from "./quill-blots/diagram-blot";
import {QuillCustomButtonHelper} from "./quill-buttons/quill-buttons.helper";
import {ContextMenu} from "primeng/contextmenu";
import {DiagramDto, DrawIoDto} from "../../../../dto/drawio.dto";
import {MvsFormFieldBaseComponent} from "../../../component/mvs-form-field-base/mvs-form-field-base.component";
import {ObjectTypeDto} from "../../../../../cc/model/dto/entity/object-type.dto";
import {
    DmDocumentAssignmentService
} from "../../../../../../feature-core/dm/service/api/dm-document-assignment.service";
import {MvsMessageService} from "../../../../service/message/mvs-message.service";
import {DmDocumentService} from "../../../../../../feature-core/dm/service/api/dm-document.service";
import {ObjectTypeService} from "../../../../../cc/service/api/object-type.service";
import {MvsCoreService} from "../../../../service/mvs-core.service";
import {FieldStorageService} from "../../service/field-storage.service";
import {ObjectRequestList} from "../../../../dto/object/request/object-request-list";
import {DtoList} from "../../../../dto/dto.list";
import Inline from "quill/blots/inline";
import {MetaService} from "../../../../meta/service/meta.service";
import {from, of, switchMap} from "rxjs";
import {UiWidgetCacheHelper} from "../../../../../ui/service/api/ui-widget-cache.service";
import {tap} from "rxjs/operators";

interface MetaAttrItem {
    name: string;                       // what you display in the dropdown
    type: 'attribute' | 'join';        // distinguishes behavior
    dto: any;                          // raw DTO (attribute or join)
}


export class MetaMentionBlot extends Inline {
    static blotName = 'meta-mention';
    static tagName = 'SPAN';
    static className = 'meta-mention-chip';

    static create(value) {
        const node = super.create();

        node.dataset.entity = value.entity;
        if (value.attribute)
            node.dataset.attribute = value.attribute;

        node.innerText = value.attribute
            ? `@meta:${value.entity}:${value.attribute}`
            : `@meta:${value.entity}`;

        return node;
    }

    static formats(node) {
        return {
            entity: node.dataset.entity,
            attribute: node.dataset.attribute || null
        };
    }
}

Quill.register(DiagramBlot, true);
Quill.register(MetaMentionBlot, true);

@Component({
    selector: 'mvs-form-field-quill-editor',
    styleUrls: ['./mvs-form-field-quill-editor.component.scss'],
    templateUrl: 'mvs-form-field-quill-editor.component.html',
    standalone: false
})
export class MvsFormFieldQuillEditorComponent
    extends MvsFormFieldBaseComponent
    implements OnInit, OnDestroy, OnChanges {
    @Input() pageId: number;

    drawIOData: DrawIoDto;
    diagramXML: DiagramDto;
    quill: Quill;
    objectTypes: ObjectTypeDto[];
    selectedDiagram: any;
    diagramContainer: HTMLElement
    openBottomBar: boolean = false;
    fullDialog: boolean = false;
    contextMenuItems = [
        {
            label: 'Edit Diagram',
            icon: 'pi pi-pencil',
            command: () => this.editDiagram()
        },

    ];
    toolBarOptions = [
        ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
        // ['blockquote', 'code-block'],
        // ['link', 'image', 'video', 'formula'],
        ['image'],

        [{'header': 1}, {'header': 2}],               // custom button values
        // [{'list': 'ordered'}, {'list': 'bullet'}, {'list': 'check'}],
        [{'list': 'ordered'}, {'list': 'bullet'}],
        // [{'script': 'sub'}, {'script': 'super'}],      // superscript/subscript
        [{'indent': '-1'}, {'indent': '+1'}],          // outdent/indent
        // [{'direction': 'rtl'}],                         // text direction

        // [{'size': ['small', false, 'large', 'huge']}],  // custom dropdown
        [{'header': [1, 2, 3, 4, 5, 6, false]}],

        // [{'color': []}, {'background': []}],          // dropdown with defaults from theme
        // [{'font': []}],
        [{'align': []}],
        // ['clean']                                         // remove formatting button
    ];

    activeEntity: string | null = null;
    mentionBlotForAttribute: any = null;
    attributeStartIndex: number = 0;

    attributeMenuOpen = false;
    // attributeItems: string[] = [];
    attributeItems: MetaAttrItem[] = [];
    attributeSelectedIndex = 0;
    // attributesJoinMap = new Map<string, string[]>();
    attributesJoinMap = new Map<string, MetaAttrItem[]>();
    isProgrammatic = false;
    allAttributeItems: MetaAttrItem[] = [];

    @ViewChild('editorHost', {static: true}) editorHost: ElementRef;
    @Output() onCancelClick = new EventEmitter<void>();
    @Output() onSaveClick = new EventEmitter<string>();
    @Output() onEditorBusy = new EventEmitter<boolean>();


    constructor(protected documentAssignmentService: DmDocumentAssignmentService,
                protected messageService: MvsMessageService,
                protected documentService: DmDocumentService,
                protected objectTypeService: ObjectTypeService,
                protected metaService: MetaService,
                coreService: MvsCoreService,
                fieldStorageService: FieldStorageService
    ) {
        super(coreService, fieldStorageService);
    }

    ngOnInit() {
        this.initComponent();

    }

    /**
     * Initialize Component.
     */
    initComponent() {
        this.refreshComponent();
        this.getObjectTypeReference();
        this.initializeQuill();
    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (!this.initialized) {
            return;
        }

    }

    /**
     * Refresh Component.
     */
    refreshComponent() {
        this.initialized = true;
        this.selectedDiagram = undefined;
        this.diagramXML = undefined;
        this.diagramContainer = undefined;
        this.drawIOData = undefined;
    }

    initializeQuill() {
        this.editorHost.nativeElement.addEventListener(
            "paste",
            this.onPaste.bind(this),
            true
        );

        this.quill = new Quill(this.editorHost.nativeElement, {
            modules: {
                syntax: {hljs},
                //toolbar option and handling images upload
                toolbar: {
                    container: this.toolBarOptions, // your button layout
                    handlers: {
                        image: () => this.customImageHandler(),
                    }
                },
                //image resizing
                imageResize: {},
                // mentioning
                mention: {

                    allowedChars: /^[A-Za-z0-9_.:]*$/,
                    mentionDenotationChars: ['@meta:'],
                    source: (searchTerm: string, renderList: Function) => {

                        if (!this.objectTypes || !this.objectTypes.length) {
                            return;
                        }

                        const matches = this.objectTypes
                            .filter(item => item.alias?.toLowerCase().includes(searchTerm.toLowerCase()))
                            .map(item => ({
                                ...item,
                                value: item.alias
                            }));

                        renderList(matches, searchTerm);
                    },
                    renderItem: (item: ObjectTypeDto) => {
                        return `${item.alias}`;
                    },
                    onSelect: function (item, insertItem) {
                        const alias = item.value;
                        const denotation = '@meta:';
                        const fullText = denotation + alias;

                        insertItem({
                            ...item,
                            fullText: fullText,
                            denotationChar: denotation,
                        });
                    }
                },
            },
            theme: 'snow'
        });

        QuillCustomButtonHelper.addInsertDiagramButton(this.quill, this.openDrawio.bind(this));

        QuillCustomButtonHelper.addCustomButtons(
            this.quill,
            () => this.handleCancel(),
            () => this.handleSave()
        );


        // Set initial value from formGroup

        // this.quill.root.innerHTML = this.formGroup?.get(this.formField.id)?.value;
        this.isProgrammatic = true;
        this.quill.root.innerHTML = this.formGroup?.get(this.formField.id)?.value;
        this.isProgrammatic = false;

        setTimeout(() => {
            try {
                const length = this.quill.getLength();
                this.quill.setSelection(length, 0);
            } catch (e) {
            }
        }, 0);

        this.quill.on('text-change', (delta, oldDelta, source) => {
            if (this.isProgrammatic) {
                return;
            }

            this.isProgrammatic = true;
            console.log('Edit mode', this.quill.root.innerHTML)
            const html = this.quill.root.innerHTML;

            this.formGroup?.get(this.formField.id)?.setValue(html);

            this.handleFieldChange({
                fieldId: this.formField.id,
                value: html,
            });

            this.isProgrammatic = false;
        });

        this.quill.on('text-change', (delta, oldDelta, source) => {

            if (source !== 'user') {
                return;
            }

            const range = this.quill.getSelection();
            if (!range) {
                return;
            }

            // Detect typed char
            const typedChar = this.quill.getText(range.index - 1, 1);
            if (typedChar !== ':') {
                return;
            }

            if (this.isProgrammatic) {
                return;
            }

            // Check the leaf before the colon
            const [leafBefore] = this.quill.getLeaf(range.index - 2);

            // If colon typed after a mention chip, trigger attribute mode
            if (leafBefore && leafBefore.domNode['dataset']?.denotationChar) {
                const entity = leafBefore.domNode['dataset'].value;  // Quill mention stores entity here
                if (!this.attributeMenuOpen) {
                    this.startAttributeMode(entity, leafBefore, range.index);
                }

            }
        });

        this.quill.root.addEventListener("keydown", (evt: KeyboardEvent) => {
            if (!this.attributeMenuOpen) {
                return;
            }

            if (evt.key === "ArrowDown") {
                evt.preventDefault();
                if (this.attributeItems.length === 0) return;
                const next =
                    (this.attributeSelectedIndex + 1) % this.attributeItems.length;
                this.highlightAttributeItem(next);
            } else if (evt.key === "ArrowUp") {
                evt.preventDefault();
                if (this.attributeItems.length === 0) return;
                const prev =
                    (this.attributeSelectedIndex - 1 + this.attributeItems.length) %
                    this.attributeItems.length;
                this.highlightAttributeItem(prev);
            } else if (evt.key === "Enter") {
                evt.preventDefault();
                this.applyAttributeFromIndex(this.attributeSelectedIndex);
            } else if (evt.key === "Escape") {
                evt.preventDefault();
                this.hideAttributeDropdown();
            }
        });

        this.quill.on('text-change', (delta, oldDelta, source) => {
            if (!this.attributeMenuOpen) return;
            if (source !== 'user') return;

            const range = this.quill.getSelection();
            if (!range) return;

            // Extract everything typed after the ":"
            const typed = this.quill.getText(this.attributeStartIndex, range.index - this.attributeStartIndex);

            const searchTerm = typed.trim().toLowerCase();

            // Filter current items
            const filtered = this.attributeItems.filter(item =>
                item.name.toLowerCase().includes(searchTerm)
            );

            // Re-render the dropdown
            this.showAttributeDropdown(filtered);
        });

        this.detectFormChanges();
    }

    startAttributeMode(entityAlias: string, mentionBlot: any, colonIndex: number) {
        this.activeEntity = entityAlias;
        this.mentionBlotForAttribute = mentionBlot;
        this.attributeStartIndex = colonIndex;

        this.loadAttributes(entityAlias);
    }

    override detectFormChanges() {

    }

    // loadAttributes(entityAlias: string) {
    //
    //     if (this.attributesJoinMap.get(entityAlias)) {
    //         const values = this.attributesJoinMap.get(entityAlias);
    //         this.showAttributeDropdown(values);
    //
    //     } else {
    //         this.metaService.getByAlias(entityAlias, true, true).subscribe((res) => {
    //             const joins = res.joins;
    //             const attributes = res.attributes;
    //
    //             const entries: string[] = [];
    //
    //             for (let join of joins) {
    //                 entries.push(join.name);
    //             }
    //
    //             for (let attr of attributes) {
    //                 entries.push(attr.attributeName);
    //             }
    //
    //             this.attributesJoinMap.set(entityAlias, entries);
    //
    //             this.showAttributeDropdown(entries);
    //
    //         });
    //     }
    // }

    loadAttributes(entityAlias: string) {

        const cached = this.attributesJoinMap.get(entityAlias);
        if (cached) {
            this.showAttributeDropdown(cached);
            return;
        }

        this.metaService.getByAlias(entityAlias, true, true).subscribe((res) => {
            const joins = res.joins;
            const attributes = res.attributes;

            const items: MetaAttrItem[] = [];

            // Joins → type: 'join', id from joinObjectTypeId
            for (let join of joins) {
                items.push({
                    name: join.name,
                    type: 'join',
                    dto: join
                });
            }

            // Attributes → type: 'attribute', id from root objectTypeId
            for (let attr of attributes) {
                items.push({
                    name: attr.attributeName,
                    type: 'attribute',
                    dto: {
                        ...attr,
                        objectTypeId: res.objectTypeId   // attach so we can use it later
                    }
                });
            }

            this.attributesJoinMap.set(entityAlias, items);
            this.allAttributeItems = items;
            this.showAttributeDropdown(items);
        });
    }

    // showAttributeDropdown(attrs: string[]) {
    //     const container = document.getElementById("attr-menu") as HTMLDivElement | null;
    //     if (!container) return;
    //
    //     const list = container.querySelector("ul.ql-mention-list") as HTMLUListElement | null;
    //     if (!list) return;
    //
    //     this.attributeItems = attrs;
    //     this.attributeMenuOpen = attrs.length > 0;
    //     this.attributeSelectedIndex = attrs.length > 0 ? 0 : -1;
    //
    //     list.innerHTML = "";
    //
    //     attrs.forEach((a, i) => {
    //         const li = document.createElement("li");
    //         li.className = "ql-mention-list-item";
    //         li.textContent = a;
    //         li.dataset.index = String(i);
    //
    //         if (i === this.attributeSelectedIndex) {
    //             li.classList.add("selected");
    //         }
    //
    //         // Use dataset index instead of closure-bound i
    //         li.onclick = () => {
    //             this.applyAttributeFromIndex(Number(li.dataset.index));
    //         };
    //
    //         li.onmouseenter = () => {
    //             this.highlightAttributeItem(Number(li.dataset.index));
    //         };
    //
    //         list.appendChild(li);
    //     });
    //
    //     if (!this.attributeMenuOpen) {
    //         container.classList.add("hidden");
    //         return;
    //     }
    //
    //     // Position relative to the Quill container (same idea as mention.ts)
    //     const range = this.quill.getSelection(true);
    //     if (!range) return;
    //
    //     const bounds = this.quill.getBounds(range.index);
    //     const quillRect = this.quill.container.getBoundingClientRect();
    //
    //     container.style.position = "absolute";
    //     container.style.top = `${bounds.bottom}px`;
    //     container.style.left = `${bounds.left}px`;
    //
    //     container.classList.remove("hidden");
    // }

    showAttributeDropdown(attrs: MetaAttrItem[]) {
        const container = document.getElementById("attr-menu") as HTMLDivElement | null;
        if (!container) return;

        const list = container.querySelector("ul.ql-mention-list") as HTMLUListElement | null;
        if (!list) return;

        this.attributeItems = attrs;
        this.attributeMenuOpen = attrs.length > 0;
        this.attributeSelectedIndex = attrs.length > 0 ? 0 : -1;

        list.innerHTML = "";

        attrs.forEach((a, i) => {
            const li = document.createElement("li");
            li.className = "ql-mention-list-item";
            li.textContent = a.name;              // ⬅️ changed
            li.dataset.index = String(i);

            if (i === this.attributeSelectedIndex) {
                li.classList.add("selected");
            }

            li.onclick = () => {
                this.applyAttributeFromIndex(Number(li.dataset.index));
            };

            li.onmouseenter = () => {
                this.highlightAttributeItem(Number(li.dataset.index));
            };

            list.appendChild(li);
        });

        if (!this.attributeMenuOpen) {
            container.classList.add("hidden");
            return;
        }

        const range = this.quill.getSelection(true);
        if (!range) return;

        const bounds = this.quill.getBounds(range.index);

        container.style.position = "absolute";
        container.style.top = `${bounds.bottom * 2}px`;
        container.style.left = `${bounds.left}px`;

        container.classList.remove("hidden");
    }

    private highlightAttributeItem(index: number) {
        const container = document.getElementById("attr-menu");
        if (!container) return;

        const list = container.querySelector("ul.ql-mention-list");
        if (!list) return;

        const items = Array.from(list.querySelectorAll(".ql-mention-list-item"));

        items.forEach((el, i) => {
            el.classList.toggle("selected", i === index);
        });

        this.attributeSelectedIndex = index;
        this.scrollAttributeItemIntoView(index);
    }

    // applyAttributeFromIndex(index: number) {
    //     if (index < 0 || index >= this.attributeItems.length) return;
    //     const attr = this.attributeItems[index];
    //     this.applyAttribute(attr);
    //     this.hideAttributeDropdown();
    // }

    applyAttributeFromIndex(index: number) {
        if (index < 0 || index >= this.attributeItems.length) return;
        const item = this.attributeItems[index];
        this.applyAttribute(item);
        this.hideAttributeDropdown();
    }



    // applyAttribute(attr: string) {
    //     const q = this.quill;
    //
    //     // Remove the ':' typed by user
    //     q.deleteText(this.attributeStartIndex - 1, 1);
    //
    //     // Remove old mention chip
    //     const blotIndex = this.mentionBlotForAttribute.offset(q.scroll);
    //     q.deleteText(blotIndex, 1);
    //
    //     // Insert new expanded chip
    //     q.insertEmbed(blotIndex, 'mention', {
    //         id: Date.now(),                     // or any unique number
    //         value: `${this.activeEntity}:${attr}`,
    //         denotationChar: '@meta:',
    //         fullText: `@meta:${this.activeEntity}:${attr}`
    //     });
    //
    //     // Cursor after chip
    //     q.setSelection(blotIndex + 1);
    //
    //     // Hide menu
    //     document.getElementById("attr-menu")?.classList.add("hidden");
    //
    //     // Cleanup
    //     this.activeEntity = null;
    //     this.mentionBlotForAttribute = null;
    // }

    // applyAttribute(item: MetaAttrItem) {
    //     const q = this.quill;
    //
    //     const name = item.name;
    //     const isJoin = item.type === 'join';
    //
    //     // 👇 data-id logic:
    //     // join  -> joinObjectTypeId
    //     // attr  -> objectTypeId (we attached this in loadAttributes)
    //     const dataId = isJoin
    //         ? item.dto.joinObjectTypeId
    //         : item.dto.objectTypeId;
    //
    //     // Remove the ':' typed by user
    //     q.deleteText(this.attributeStartIndex - 1, 1);
    //
    //     // Remove old mention chip
    //     const blotIndex = this.mentionBlotForAttribute.offset(q.scroll);
    //     q.deleteText(blotIndex, 1);
    //
    //     // Insert new expanded chip with correct id
    //     q.insertEmbed(blotIndex, 'mention', {
    //         id: dataId,  // ⬅️ this becomes data-id on the span
    //         value: `${this.activeEntity}:${name}`,
    //         denotationChar: '@meta:',
    //         fullText: `@meta:${this.activeEntity}:${name}`
    //     });
    //
    //     // Cursor after chip
    //     q.setSelection(blotIndex + 1);
    //
    //     document.getElementById("attr-menu")?.classList.add("hidden");
    //
    //     // Cleanup
    //     this.activeEntity = null;
    //     this.mentionBlotForAttribute = null;
    // }

    applyAttribute(item: MetaAttrItem) {
        const q = this.quill;
        const name = item.name;
        const isJoin = item.type === 'join';

        const dataId = isJoin
            ? item.dto.joinObjectTypeId
            : item.dto.objectTypeId;

        // 1️⃣ Remove the ':' + typed searchTerm
        const range = q.getSelection();
        const typedLength = range.index - this.attributeStartIndex;

        // delete searchTerm: "cal"
        if (typedLength > 0) {
            q.deleteText(this.attributeStartIndex, typedLength);
        }

        // delete the ':' before searchTerm
        q.deleteText(this.attributeStartIndex - 1, 1);

        // 2️⃣ Remove the old mention chip
        const blotIndex = this.mentionBlotForAttribute.offset(q.scroll);
        q.deleteText(blotIndex, 1);

        // 3️⃣ Insert new expanded chip
        q.insertEmbed(blotIndex, 'mention', {
            id: dataId,
            value: `${this.activeEntity}:${name}`,
            denotationChar: '@meta:',
            fullText: `@meta:${this.activeEntity}:${name}`
        });

        q.setSelection(blotIndex + 1);

        document.getElementById("attr-menu")?.classList.add("hidden");

        this.activeEntity = null;
        this.mentionBlotForAttribute = null;
    }

    hideAttributeDropdown() {
        const container = document.getElementById("attr-menu") as HTMLDivElement | null;
        if (!container) return;

        this.attributeMenuOpen = false;
        this.attributeItems = [];
        this.attributeSelectedIndex = -1;

        container.classList.add("hidden");
    }

    private scrollAttributeItemIntoView(index: number) {
        const container = document.getElementById("attr-menu") as HTMLDivElement;
        if (!container) return;

        const list = container.querySelector("ul.ql-mention-list") as HTMLUListElement;
        if (!list) return;

        const items = list.querySelectorAll(".ql-mention-list-item") as NodeListOf<HTMLLIElement>;
        const item = items[index];
        if (!item) return;

        const containerHeight = container.offsetHeight;
        const itemHeight = item.offsetHeight;
        const itemTop = item.offsetTop;
        const itemBottom = itemTop + itemHeight;

        const scrollTop = container.scrollTop;
        const scrollBottom = scrollTop + containerHeight;

        // If item is above visible area → scroll up
        if (itemTop < scrollTop) {
            container.scrollTop = itemTop;
        }
        // If item is below visible area → scroll down
        else if (itemBottom > scrollBottom) {
            container.scrollTop = itemBottom - containerHeight;
        }
    }

    getObjectTypeReference() {
        this.getObjectTypes().subscribe({
            next: (types) => {
                this.objectTypes = types;
            },
            error: err => console.error(err)
        });
    }

    public getObjectTypes() {
        const objectTypeReference = 'cc.CcObjectTypeReference';
        return from(UiWidgetCacheHelper.get(objectTypeReference)).pipe(
            switchMap((cached: any) => {
                if (cached) {
                    return of(cached);
                }
                return this.objectTypeService.list(ObjectRequestList.createSimple(null)).pipe(
                    tap(value => {
                        const dtoData = <DtoList<ObjectTypeDto>>value;
                        dtoData.sortBy("alias", true);
                        this.objectTypes = dtoData.entries;

                        void UiWidgetCacheHelper.set(objectTypeReference, this.objectTypes).catch(err => {

                            }
                        );
                    })
                );
            })
        );
    }


    onFormValueChange(value: any) {
        if (this.isProgrammatic) {
            return;
        }

        if (this.quill && value !== this.quill.root.innerHTML) {
            // this.quill.root.innerHTML = value || '';
            this.isProgrammatic = true;
            this.quill.root.innerHTML = value || '';
            this.isProgrammatic = false;
        }
    }

    customImageHandler() {
        try {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/*'); // Allow only image files

            input.onchange = async () => {
                this.busy = true;
                const file = input.files ? input.files[0] : null;
                await this.uploadFile(file);
                this.busy = false;
            };

            input.click(); // Trigger the file input dialog
        } catch (err) {
            console.log(err);
        }

    }

    handleEditorBusy(busy: boolean) {
        this.onEditorBusy.emit(busy);
    }

    async uploadFile(file: File, drawIOData?: DrawIoDto) {
        this.handleEditorBusy(true);
        const documentId: number = await QuillHelper.uploadFile(file, 'image', this.documentAssignmentService, this.pageId);
        this.handleEditorBusy(false);
        if (documentId) {
            const fileURL = await QuillHelper.fileUrl(documentId, this.documentService, file.type);

            if (fileURL && !drawIOData && drawIOData?.delta == null) {
                QuillHelper.insertIntoEditor(this.quill, file.type, file.name, fileURL as string)
            } else if (fileURL && drawIOData && drawIOData?.delta == null) {
                QuillHelper.insertIntoEditor(this.quill, file.type, file.name, fileURL as string, drawIOData)
            } else if (drawIOData.delta > -1) {
                const ops = this.quill.getContents().ops;
                console.log(ops);
                let indexOffset = 0;

                for (let i = 0; i < ops.length; i++) {
                    const op = ops[i];

                    if (i === drawIOData.delta && op.insert && op.insert['diagram']) {
                        const newDiagram = {
                            diagram: {
                                src: fileURL,
                                xml: drawIOData.xml
                            }
                        };
                        console.log(indexOffset);

                        const delta = new Delta()
                            .retain(indexOffset) // Skip to the diagram
                            .delete(1)           // Delete the old diagram embed
                            .insert(newDiagram); // Insert the updated one

                        this.quill.updateContents(delta);
                        break;
                    }

                    // Update offset for next iteration
                    if (typeof op.insert === 'string') {
                        indexOffset += op.insert.length;
                    } else {
                        indexOffset += 1; // For embeds like diagram, image, etc.
                    }
                }
            }
            this.refreshComponent();
        }
    }


    openDrawio(action?: string) {
        this.openBottomBar = !this.openBottomBar;

    }

    async handleDrawIoData(event: any) {
        this.drawIOData = event;
        if (this.drawIOData && this.drawIOData.data) {
            const file = QuillHelper.base64ToFile(this.drawIOData.data, 'newFile');
            await this.uploadFile(file, this.drawIOData);
            this.openBottomBar = false;
        }
    }

    onEditorRightClick(event: MouseEvent, cm: ContextMenu) {
        const target = event.target as HTMLElement;
        this.diagramContainer = target.closest('.ql-diagram') as HTMLElement;
        if (this.diagramContainer) {
            // Optionally store selected diagram reference
            this.selectedDiagram = {
                src: this.diagramContainer.getAttribute('src'),
                xml: this.diagramContainer.getAttribute('data-xml'),
            };

            cm.show(event);
        }
    }

    editDiagram() {
        let deltaIdx = -1;
        this.quill.getContents().forEach((content, index) => {
            if (content && content.insert && content.insert["diagram"] && content.insert["diagram"].src == this.selectedDiagram.src) {
                deltaIdx = index;
            }
        });
        if (deltaIdx > -1) {
            this.diagramXML = {xml: this.selectedDiagram.xml, delta: deltaIdx};
            this.openDrawio('edit');
        }
    }

    handleFullDialog() {
        this.fullDialog = !this.fullDialog;
    }

    async onPaste(event: ClipboardEvent) {
        if (!event.clipboardData) return;

        const items = event.clipboardData.items;

        for (let i = 0; i < items.length; i++) {
            const item = items[i];

            // Screenshot or pasted image
            if (item.type.startsWith("image/")) {

                // STOP QUILL FROM INSERTING BASE64
                event.preventDefault();
                event.stopPropagation();

                const blob = item.getAsFile();
                if (!blob) return;

                const file = new File([blob], "pasted-image.png", {type: blob.type});

                // Use your existing upload flow (same as toolbar)
                await this.uploadFile(file);

                return; // Do NOT let Quill process anything
            }
        }
    }

    handleCancel() {
        this.onCancelClick.emit();
    }

    handleSave() {
        this.handleEditorBusy(true);
        const content = this.quill.root.innerHTML;
        this.onSaveClick.emit(content);
    }

}
